import { getSession } from '@lib/auth/client';

export interface WaitForSessionOptions {
  /**
   * Polling interval in milliseconds.
   * How frequently to check for a valid session.
   * @default 100
   */
  interval?: number;

  /**
   * Maximum time to wait in milliseconds before timing out.
   * @default 5000
   */
  timeout?: number;
}

export class SessionTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(
      `Timed out waiting for session after ${timeoutMs}ms. The session may not have been established.`
    );
    this.name = 'SessionTimeoutError';
  }
}

function hasAuthenticatedSession(
  session: Awaited<ReturnType<typeof getSession>>
): session is NonNullable<Awaited<ReturnType<typeof getSession>>> {
  return !!session?.user?.id;
}

/**
 * Polls for a valid NextAuth session after sign-in.
 *
 * After NextAuth's signIn() resolves, the JWT cookie is set server-side,
 * but there's a brief window where subsequent requests may not yet include
 * the new credentials. This utility polls getSession() until a valid session
 * is observed, avoiding race conditions with hardcoded timeouts.
 *
 * @param options - Configuration for polling interval and timeout
 * @returns Promise that resolves with the session when available
 * @throws {SessionTimeoutError} if no session is found within the timeout period
 *
 * @example
 * ```typescript
 * import { waitForSession, SessionTimeoutError } from '@utils/wait-for-session';
 *
 * const signInResult = await signIn('credentials', { redirect: false, ... });
 *
 * if (!signInResult?.error) {
 *   try {
 *     const session = await waitForSession({ interval: 100, timeout: 5000 });
 *     // Session is now available, safe to make authenticated requests
 *     await apolloClient.resetStore();
 *   } catch (error) {
 *     if (error instanceof SessionTimeoutError) {
 *       // Handle timeout - session never became available
 *     }
 *   }
 * }
 * ```
 */
export async function waitForSession(
  options: WaitForSessionOptions = {}
): Promise<Awaited<ReturnType<typeof getSession>>> {
  const { interval = 100, timeout = 5000 } = options;

  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    const checkSession = async () => {
      try {
        const session = await getSession();

        if (hasAuthenticatedSession(session)) {
          resolve(session);
          return;
        }

        const elapsedTime = Date.now() - startTime;
        if (elapsedTime >= timeout) {
          reject(new SessionTimeoutError(timeout));
          return;
        }

        // Schedule next check
        setTimeout(checkSession, interval);
      } catch (error) {
        // If getSession throws, reject with the original error
        reject(error);
      }
    };

    // Start polling immediately
    checkSession();
  });
}
